import { Component, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { Exception, ToasterService, Validator } from '@anddone/ui-utilities';
import { NgbAccordionModule, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../../../../services/user-service';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { MessageConstants } from '../../../../../constants/message.constant';
import { UserTypeEnum } from '../../../../../enum/user-type.enum';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-edit-user',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgbAccordionModule,
    CommonModule,
    TranslateModule,
  ],
  templateUrl: './edit-user.html',
  styleUrl: './edit-user.scss',
  providers: [provideNgxMask()],
})
export class EditUser implements OnInit {
  isLoadingEditUser = signal<boolean>(false);
  // Form variables
  editUserForm: any;
  editUserFormErrors: any = {};

  // Other
  userDetails: any;
  loggedInUserData: any;
  validator: Validator;

  config = {
    Phone: {
      required: { name: ValidationConstant.user.add.phone.name },
      maxlength: {
        name: ValidationConstant.user.add.phone.name,
        max: ValidationConstant.user.add.phone.mask_maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.user.add.phone.name,
        min: ValidationConstant.user.add.phone.mask_minLength.toString(),
      },
      pattern: { name: ValidationConstant.user.add.phone.name },
    },
    Email: {
      required: { name: ValidationConstant.user.add.email.name },
      maxlength: {
        name: ValidationConstant.user.add.email.name,
        max: ValidationConstant.user.add.email.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.user.add.email.name,
        min: ValidationConstant.user.add.email.minLength.toString(),
      },
      pattern: { name: ValidationConstant.user.add.email.name },
    },
  };

  public activeModal = inject(NgbActiveModal);
  private formBuilder = inject(FormBuilder);
  private allyCommonService = inject(AllyCommonService);
  private userService = inject(UserService);
  private toasterService = inject(ToasterService);
  constructor() {
    this.validator = new Validator(this.config);
  }

  ngOnInit(): void {
    this.userDetails = this.userService.getUserDetails();
    this.userDetails = this.userDetails.source._value;
    this.loggedInUserData = this.allyCommonService.getLoggedInData() || '';
    this.editUserForm = this.formBuilder.group({
      Phone: [
        this.userDetails.contact?.phone,
        [
          Validators.required,
          Validators.maxLength(ValidationConstant.user.add.phone.maxLength),
          Validators.minLength(ValidationConstant.user.add.phone.minLength),
          Validators.pattern(ValidationConstant.phoneFormat_regex),
        ],
      ],
      Email: [
        this.userDetails.contact?.email,
        [
          Validators.required,
          Validators.maxLength(ValidationConstant.user.add.email.maxLength),
          Validators.pattern(ValidationConstant.email_regex),
        ],
      ],
    });

    this.editUserForm.valueChanges.subscribe((_data: any) => this.onValueChanged('editUserForm'));
  }

  onValueChanged(data?: any) {
    if (data === 'editUserForm') {
      if (!this.editUserForm) {
        return;
      }
      this.editUserFormErrors = this.validator.validate(this.editUserForm);
    }
  }

  close() {
    this.activeModal.close('Modal closed');
  }

  editUser() {
    this.validator.validate(this.editUserForm);
    this.editUserFormErrors = this.validator.validate(this.editUserForm);
    if (this.editUserForm.invalid) {
      return;
    }
    this.isLoadingEditUser.set(true);
    const reqObj: any = {};
    reqObj.parentId = this.loggedInUserData?.id;
    reqObj.userName = this.userDetails.userName;
    reqObj.userAuthenticationType = this.userDetails.userAuthenticationType;
    reqObj.isAdmin = true;
    reqObj.userType = UserTypeEnum.ALLY;
    reqObj.contact = {
      name: {
        firstName: this.userDetails.contact?.name.firstName,
        lastName: this.userDetails.contact?.name.lastName,
      },
      phone: this.editUserForm.value.Phone,
      email: this.editUserForm.value.Email,
      address: this.createAddressObject(),
    };
    this.userService.editUser(reqObj, this.userDetails.id, this.loggedInUserData?.id).subscribe({
      next: (_res: any) => {
        this.editUserForm.reset();
        this.isLoadingEditUser.set(false);
        this.toasterService.showSuccess(MessageConstants.user.edituserSuccess, 'full');
        this.userService.updateUserDetails(true);
        this.close();
      },
      error: (e: any) => {
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
        this.isLoadingEditUser.set(false);
      },
    });
  }

  createAddressObject() {
    const address: any = {};
    address.addressLine1 = this.userDetails.contact?.address.addressLine1;
    address.addressLine2 = this.userDetails.contact?.address.addressLine2;
    address.city = this.userDetails.contact?.address.city;
    address.state = this.userDetails.contact?.address.state;
    address.country = this.userDetails.contact?.address.country;
    address.postalCode = this.userDetails.contact?.address.postalCode;
    return Object.prototype.hasOwnProperty.call(address, 'country') ? address : null;
  }
}
